import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Header, Footer } from "../components/Chrome";
import { useSettings } from "../lib/settings";
import { api, formatError } from "../lib/api";
import { toast } from "sonner";

export default function Enquiry() {
  const { settings } = useSettings();
  const [f, setF] = useState({
    business_name: "", contact_name: "", phone: "", email: "",
    city: "", products_of_interest: "", estimated_quantities: "", notes: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [refNo, setRefNo] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/enquiries", f);
      setRefNo(data.inquiry_no);
      setSent(true);
      toast.success(`Enquiry #${data.inquiry_no} sent. We'll be in touch within 1 business day.`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-paper text-ink">
      <Header />
      <section className="pt-28 md:pt-32 pb-8 px-6 md:px-12 lg:px-16" data-testid="enquiry-hero">
        <div className="overline text-black/50 mb-6">Wholesale Enquiry</div>
        <h1 className="font-display text-[13vw] md:text-[10vw] lg:text-[9vw] uppercase leading-[0.85] tracking-tighter">
          Request<br/><em className="font-editorial italic tracking-normal">pricing.</em>
        </h1>
      </section>

      <section className="px-6 md:px-12 lg:px-16 pb-20 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          <div className="lg:col-span-4 lg:sticky lg:top-32 self-start space-y-6">
            <p className="text-lg leading-relaxed">
              Tell us about your business and what you're looking to stock. We'll follow up within one business day with pricing, availability and next steps.
            </p>
            <div className="border border-ink p-5">
              <div className="overline text-black/50 mb-2">Need to chat first?</div>
              {settings?.show_phone && settings?.phone && <div className="text-sm">{settings.phone}</div>}
              {settings?.show_email && settings?.public_email && <div className="text-sm">{settings.public_email}</div>}
              <Link to="/contact" className="link-underline text-sm font-semibold mt-3 inline-block" data-testid="enquiry-to-contact">Use contact form →</Link>
            </div>
          </div>

          {sent ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="lg:col-span-7 lg:col-start-6 border border-ink p-8 md:p-12 hard-shadow bg-paper"
              data-testid="enquiry-success"
            >
              <div className="overline text-cobalt mb-4">Received · Ref #{refNo}</div>
              <h2 className="font-display text-5xl md:text-6xl uppercase leading-[0.9]">Thanks —<br/>we've got it.</h2>
              <p className="mt-6 text-lg leading-relaxed">Your reference number is <strong>#{refNo}</strong> — keep it handy. A confirmation is on its way to your inbox, and our team will reply within one business day with pricing and availability.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/catalog" className="btn-primary" data-testid="enquiry-catalog-btn">Browse catalog</Link>
                <Link to="/" className="btn-ghost">Back to home</Link>
              </div>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1] }}
              onSubmit={submit}
              className="lg:col-span-7 lg:col-start-6 border border-ink p-6 md:p-10 hard-shadow bg-paper space-y-5"
              data-testid="enquiry-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="overline block mb-2">Business name *</label>
                  <input required value={f.business_name} onChange={e => setF({ ...f, business_name: e.target.value })} className="field" data-testid="enq-business" />
                </div>
                <div>
                  <label className="overline block mb-2">Contact person *</label>
                  <input required value={f.contact_name} onChange={e => setF({ ...f, contact_name: e.target.value })} className="field" data-testid="enq-contact" />
                </div>
                <div>
                  <label className="overline block mb-2">Phone *</label>
                  <input required value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} className="field" data-testid="enq-phone" />
                </div>
                <div>
                  <label className="overline block mb-2">Email *</label>
                  <input required type="email" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} className="field" data-testid="enq-email" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Delivery city / location</label>
                  <input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} placeholder="e.g. Brampton, ON" className="field" data-testid="enq-city" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Products of interest</label>
                  <textarea rows={3} value={f.products_of_interest} onChange={e => setF({ ...f, products_of_interest: e.target.value })} placeholder="e.g. Energy drinks, chocolates, Banta imports" className="field" data-testid="enq-products" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Estimated quantities</label>
                  <input value={f.estimated_quantities} onChange={e => setF({ ...f, estimated_quantities: e.target.value })} placeholder="e.g. 20 cases per week" className="field" data-testid="enq-quantities" />
                </div>
                <div className="md:col-span-2">
                  <label className="overline block mb-2">Additional notes</label>
                  <textarea rows={4} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} className="field" data-testid="enq-notes" />
                </div>
              </div>
              <button disabled={busy} className="btn-primary w-full md:w-auto" data-testid="enq-submit">
                {busy ? "Sending…" : "Send enquiry →"}
              </button>
            </motion.form>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}
